import { Tooltip } from 'antd';
import _, { isEmpty } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip as BSToolTip } from 'react-bootstrap';

import { contract_info, previous_top_talent_status } from '../../../../../assets/img';
import { Switch } from '../../../../../components';
import CustomAntModal from '../../../../../components/CustomAntModal/CustomAntModal';
import { capitalizeFirstLetter, decodeBase64ToImage, formatDate, hideOtherTalentLabel } from '../../../../../utils/helper';
import { avatarSelector, profileSelector } from '../../../selector';
import styles from '../overall-profile-user.module.scss';
import NameAndPosition from './NameAndPosition';
import { USER_INFO_DETAIL } from '../constants';
import { TP_PRINT_SECTION_NAME } from '../../../../../components/TalentProfilePrintPreview/constants';

const UserInfoDetail = ({ setIsActive, isActive, isPrintPdf, isHasEditPermission, talentCategoryUpdate, isLoadingPosition }) => {
  const {
    secondSectionProfile,
    loading,
    thirdSectionProfile: { assignment_status_type, job_role_level },
  } = useSelector(profileSelector);

  const avatarImage = useSelector(avatarSelector);

  const [showAboutMePopup, setShowAboutMePopup] = useState(false);

  const displayInfoEmployee = useCallback(
    (text, text2) => {
      if (loading || isLoadingPosition) {
        return (
          <b>
            <Skeleton count={1} height={17}></Skeleton>
          </b>
        );
      } else {
        return (
          <b>
            {text} {text2}
          </b>
        );
      }
    },
    [loading, isLoadingPosition]
  );

  const displayInfoEmployeeTooltip = useCallback(
    (text, text2) => {
      if (loading || isLoadingPosition) {
        return (
          <b>
            <Skeleton count={1} height={17}></Skeleton>
          </b>
        );
      } else {
        return (
          <div>
            {text} {text2}
          </div>
        );
      }
    },
    [loading, isLoadingPosition]
  );

  const talentLabel = useMemo(() => {
    const status = talentCategoryUpdate?.currentStatus || secondSectionProfile?.topTalentStatus;
    const label = capitalizeFirstLetter(status);
    const isVPTalent = hideOtherTalentLabel(assignment_status_type, job_role_level, label);
    return isVPTalent ? displayInfoEmployee('Top Management') : displayInfoEmployee(label);
  }, [assignment_status_type, displayInfoEmployee, job_role_level, secondSectionProfile, talentCategoryUpdate]);

  const handleShowAboutMe = (data) => {
    if (!data) return;
    if (!isPrintPdf && data.length > 660) {
      return (
        <>
          {data.slice(0, 660)}...
          <span className={styles.viewMore} onKeyDown={() => {}} onClick={() => setShowAboutMePopup(true)}>
            View more
          </span>
        </>
      );
    } else {
      return data;
    }
  };

  const getYearsOfRetirementDate = useMemo(() => {
    return (
      (secondSectionProfile.yearsToRetirement > 0 && secondSectionProfile.yearsToRetirement) ||
      (secondSectionProfile.yearsContractEndDate > 0 && secondSectionProfile.yearsContractEndDate) ||
      null
    );
  }, [secondSectionProfile]);

  return (
    <>
      <div className={styles.userInfoDetail}>
        {!isPrintPdf && isHasEditPermission && (
          <div className={styles.switch}>
            <Switch setIsActive={setIsActive} isActive={isActive} />
          </div>
        )}
        {isActive && !isEmpty(secondSectionProfile) && (
          <div className={styles.listInfo}>
            <div className={styles.itemInfo} data-print-section={TP_PRINT_SECTION_NAME.AGE}>
              <p>Age</p>
              {displayInfoEmployee(secondSectionProfile?.age)}
            </div>
            <div className={styles.itemInfo} data-print-section={TP_PRINT_SECTION_NAME.DATE_OF_BIRTH}>
              <p>Date of Birth</p>
              {displayInfoEmployee(secondSectionProfile && formatDate(secondSectionProfile.dateOfBirth))}
            </div>
            <div className={styles.itemInfo} data-print-section={TP_PRINT_SECTION_NAME.RETIREMENT_DATE}>
              {secondSectionProfile?.salary_grade?.startsWith('CONTRACT') || secondSectionProfile?.salary_grade?.startsWith('EXPAT') ? (
                <p>
                  Contract End Date
                  {!isPrintPdf && (
                    <Tooltip
                      placement="top"
                      title={
                        <>
                          <div>Retirement Date : </div>
                          <div>
                            {displayInfoEmployeeTooltip(
                              <div>
                                {!_.isEmpty(secondSectionProfile.retirement_date) ? formatDate(secondSectionProfile.retirement_date) : 'N/A'}{' '}
                                {!_.isEmpty(secondSectionProfile.retirement_date) &&
                                  getYearsOfRetirementDate &&
                                  `[${getYearsOfRetirementDate} years]`}
                              </div>
                            )}
                          </div>
                        </>
                      }
                      overlayClassName="custom-tooltip"
                      {...{
                        overlayInnerStyle: {
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '12px',
                          lineHeight: '18px',
                          gap: '24px',
                          padding: '12px',
                          borderRadius: '4px',
                        },
                      }}
                    >
                      <span style={{ marginLeft: '5px' }}>
                        <img src={contract_info} alt="contract_info" style={{ cursor: 'pointer' }} />
                      </span>
                    </Tooltip>
                  )}
                </p>
              ) : (
                <p>Retirement Date</p>
              )}

              {secondSectionProfile?.salary_grade?.startsWith('CONTRACT') || secondSectionProfile?.salary_grade?.startsWith('EXPAT')
                ? displayInfoEmployee(
                    <>
                      {!_.isEmpty(secondSectionProfile.contract_end_date)
                        ? `${formatDate(secondSectionProfile.contract_end_date)} ${
                            secondSectionProfile.yearsContractEndDate ? `[${secondSectionProfile.yearsContractEndDate} years]` : '[N/A]'
                          }`
                        : 'N/A'}{' '}
                    </>
                  )
                : displayInfoEmployee(
                    <>
                      {!_.isEmpty(secondSectionProfile.retirement_date) ? formatDate(secondSectionProfile.retirement_date) : 'N/A'}{' '}
                      {getYearsOfRetirementDate && `[${getYearsOfRetirementDate} years]`}
                    </>
                  )}
            </div>
            <div className={styles.itemInfo} data-print-section={TP_PRINT_SECTION_NAME.NATIONALITY}>
              <p>Nationality</p>
              {!isPrintPdf &&
              secondSectionProfile &&
              secondSectionProfile?.country &&
              secondSectionProfile?.country === USER_INFO_DETAIL.IS_MALAYSIA ? (
                <OverlayTrigger
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <BSToolTip className="stateOfBirthTooltip">
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          lineHeight: '20px',
                          letterSpacing: '0.173px',
                          color: '#fff',
                          textAlign: 'left',
                          padding: '5px',
                        }}
                      >
                        State of Birth: {secondSectionProfile?.stateOfBirth?.trim() || 'N/A'}
                      </div>
                    </BSToolTip>
                  }
                >
                  {displayInfoEmployee(secondSectionProfile?.country)}
                </OverlayTrigger>
              ) : (
                displayInfoEmployee(secondSectionProfile?.country)
              )}
            </div>
            <div className={styles.itemInfo} data-print-section={TP_PRINT_SECTION_NAME.TOP_TALENT_CATEGORY}>
              <p>Top Talent Category</p>
              {!isPrintPdf &&
                talentCategoryUpdate?.previousStatus &&
                talentCategoryUpdate?.previousStatus !== talentCategoryUpdate?.currentStatus && (
                  <Tooltip
                    title={`Previous Top Talent Status : ${talentCategoryUpdate?.previousStatus}`}
                    overlayInnerStyle={{ borderRadius: '6px' }}
                    overlayStyle={{ width: '212px' }}
                  >
                    <img
                      src={previous_top_talent_status}
                      alt="previous_top_talent_status"
                      style={{ marginRight: '10px', width: '8px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              {talentLabel}
            </div>
          </div>
        )}
        {isActive && !isEmpty(secondSectionProfile) && (
          <div className={styles.aboutMe} data-print-section={TP_PRINT_SECTION_NAME.ABOUT_ME}>
            <p>About Me: </p>
            {(loading || isLoadingPosition) && <Skeleton count={1} height={30} width={647}></Skeleton>}
            <span>{!loading && !isLoadingPosition && secondSectionProfile && handleShowAboutMe(secondSectionProfile?.aboutMe)}</span>
          </div>
        )}
      </div>

      <CustomAntModal
        open={showAboutMePopup}
        setOpen={setShowAboutMePopup}
        width={1000}
        centered
        styles={{
          borderRadius: 8,
          closePosition: 50,
          containerPaddingLeftRight: 50,
          containerPaddingTopBottom: 50,
        }}
        hideDefaultBtn
      >
        <div className={styles.aboutMeModal}>
          <div className={styles.header}>
            <img className={styles.avatar} src={decodeBase64ToImage(avatarImage)} alt="avatar" />
            <NameAndPosition />
          </div>
          <div className={styles.detail}>
            <div className={styles.title}>About Me</div>
            <div className={styles.content}>{secondSectionProfile?.aboutMe}</div>
          </div>
        </div>
      </CustomAntModal>
    </>
  );
};

export default UserInfoDetail;
